/**
 * sanitize-next-path — Open-Redirect-Schutz für `next`-Redirect-Pfade.
 *
 * PUR (kein I/O) → unit-testbar. Vier Konsumenten teilen sich diese eine Quelle:
 * SSO-Mint (`sso-handoff.ts`), SSO-Consumer (`sso-consume.ts`), der tools-Client
 * (P1c Auto-Handoff) und der DAM/Transfer-Redirect-Helper (P1c PR-C). Verbatim aus
 * `server/lib/sso-token.ts` gehoben (P1c PR-A1, D-P1c-4); `sso-token.ts`
 * re-exportiert `sanitizeNextPath` byte-gleich → P1b-Importe/Tests unverändert.
 *
 * Eine Quelle statt Client-Duplikat: Drift auf einer Open-Redirect-Grenze wäre
 * eine Sicherheitslücke.
 */

/**
 * Enthält `s` ein Control-Char (0x00–0x1F oder 0x7F)? CR/LF/TAB darin würden
 * eine `Location`-Header-Injection erlauben. Bewusst via charCodeAt statt
 * Control-Char-Regex (keine unlesbaren Bytes im Source).
 */
export function hasControlChar(s: string): boolean {
  for (let i = 0; i < s.length; i++) {
    const code = s.charCodeAt(i);
    if (code <= 0x1f || code === 0x7f) return true;
  }
  return false;
}

/**
 * Open-Redirect-Schutz (patterns/nextjs.md §Protocol-relative +
 * patterns/api-mutations.md §URL-Validation Trap B): akzeptiert `next` NUR als
 * app-relativen Pfad. Alles andere → `/`.
 *
 * Erlaubt: beginnt mit `/`, NICHT `//` (protocol-relative), enthält keinen
 * Backslash (Browser normalisieren `\`→`/`, `/\evil` wäre protocol-relative),
 * kein Schema (fängt nicht mit `/` an → raus), keine Control-Chars.
 */
export function sanitizeNextPath(next: unknown): string {
  if (typeof next !== 'string' || next.length === 0) return '/';
  if (!next.startsWith('/')) return '/';
  if (next.startsWith('//')) return '/';
  if (next.includes('\\')) return '/';
  if (hasControlChar(next)) return '/';
  return next;
}
